import { AbsoluteCenter, Center, Spinner } from '@chakra-ui/react';
import { getPublicTemplateDesignData } from 'api/getPageDataFromTemplateId';
import { loadFonts } from 'hooks/useFont';
import { dispatchGlobalApplyThemeEvent } from 'modules/Editor/Editor.helpers';
import { ColorShuffle } from 'modules/Editor/components/WidgetMenu/SideMenu/BrandKitMenu/ApplyColorTheme/colorShuffle';
import {
  applyFont,
  labelTextWidgets,
} from 'modules/Editor/components/WidgetMenu/SideMenu/BrandKitMenu/ApplyFonts/ApplyFonts.helpers';
import {
  applyLogo,
  selectAllPageBackgroundDataFromData,
} from 'modules/Editor/components/WidgetMenu/SideMenu/BrandKitMenu/ApplyLogo/ApplyLogo.helpers';
import { setActivePage, store, useAppSelector } from 'modules/Editor/store';
import {
  selectInfographHeightPx,
  selectInfographWidthPx,
  selectPageBackgroundString,
  selectWidgets,
} from 'modules/Editor/store/infographSelector';
import {
  applyColor,
  applyFont as applyFontAction,
  applyLogo as applyLogoAction,
  loadInfograph,
} from 'modules/Editor/store/infographSlice';
import { findFontsInPage } from 'modules/Editor/store/infographSlice.helpers';
import { setFontsLoaded } from 'modules/Editor/store/pageControlSlice';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { MAX_THUMBNAIL_DIMENSIONS_RATIO } from 'modules/HRTeams/components/Template/InfographTemplate.config';
import { Page } from 'modules/common/components/Page';
import { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { PageBackgroundData } from 'types/pageTypes';
import { isDark } from 'utils/color';
import { ReadOnlyStructureTreeListWidgetRenderer } from 'widgets/WidgetRenderer';
import widgetVersionManager from 'widgets/WidgetVersionManager';
import { getPageColor } from 'widgets/sdk/PageNumber/PageNumber.helpers';

import { DEFAULT_PREVIEW_SCALE } from './BrandTemplatePreview.config';
import { addColorCache, addLogoCache, getColorCache, getLogoCache } from './BrandTemplatePreview.helpers';
import { BrandTemplatePreviewProvider, useBrandTemplatePreview } from './useBrandTemplatePreview';

type DesignPreviewLoaderProps = {
  children?: ReactNode;
  customStore?: any;
};

const getInfographState = async (brandTemplateInfographState?: string, templateId?: string) => {
  if (brandTemplateInfographState) {
    return JSON.parse(brandTemplateInfographState);
  }

  if (templateId) {
    return await getPublicTemplateDesignData(templateId);
  }

  return null;
};

const DesignPreviewLoader = ({ children, customStore }: DesignPreviewLoaderProps) => {
  const { brandTemplateData, setIsLoading, setHasError } = useBrandTemplatePreview();

  const updateTemplateLogo = useCallback(
    async (pageBackgrounds: PageBackgroundData, seed?: number) => {
      const pageBackColorIsDark = pageBackgrounds ? isDark(getPageColor(pageBackgrounds)) : false;
      if (!pageBackgrounds?.background) return;

      const { logos = [] } = brandTemplateData || {};

      if (!logos || logos?.length === 0) return;
      const { dispatch, getState } = customStore ?? store;

      const filterImages = logos.filter((logo) => {
        return logo.isDark !== pageBackColorIsDark;
      });

      if (filterImages.length === 0) return;

      const state = getState();
      const fromCache = getLogoCache(state.infograph.id);
      // do not cache logo if the new array doesn't contain the cached logo
      const isNewLogo = !!logos.find((logo) => logo.url === fromCache?.url);
      const widgets = selectWidgets(state);
      const imgIndex = Math.floor((seed || Math.random()) * filterImages.length);
      const logoImg = !isNewLogo ? filterImages[imgIndex] : fromCache || filterImages[imgIndex];
      const updateWidgets = await applyLogo(widgets, logoImg);

      if (!updateWidgets.length) return;
      addLogoCache(logoImg, state.infograph.id);

      dispatch(applyLogoAction(updateWidgets));
    },
    [brandTemplateData, customStore],
  );

  useEffect(() => {
    const {
      templateId,
      colors,
      shouldCache,
      fontConfig,
      infographState: brandTemplateInfographState,
    } = brandTemplateData || {};

    if (!templateId) {
      setHasError(true);
      return;
    }

    setHasError(false);

    async function fetchData(infographStore: any) {
      if (!templateId && !brandTemplateInfographState) return;

      try {
        setIsLoading(true);

        const infographState = await getInfographState(brandTemplateInfographState, templateId);

        // Simple validation for infograph state
        if (!infographState || !infographState.id || !infographState.pages || !infographState.widgets) return;

        const pageId = infographState?.pageOrder?.[0] || '';

        if (!pageId) return;

        const { dispatch, getState } = infographStore;

        const sessionRandomNumber = sessionStorage.getItem('sessionRandomNumber');
        let seed = undefined;

        if (shouldCache) {
          seed = sessionRandomNumber ? Number(sessionRandomNumber) : Math.random();

          if (!sessionRandomNumber) {
            sessionStorage.setItem('sessionRandomNumber', String(seed));
          }
        }

        // update widgets to latest version
        const updatedWidgets = widgetVersionManager.update(infographState.widgets);
        infographState.widgets = updatedWidgets;

        dispatch(loadInfograph(infographState));

        const updateTemplateFont = () => {
          if (!fontConfig) return;

          const state = getState();
          const labeledTextWidgets = labelTextWidgets(state.infograph);
          if (!labeledTextWidgets.length) return;

          const widgets = selectWidgets(state);
          const updateWidgets = applyFont(widgets, labeledTextWidgets, fontConfig);

          if (!updateWidgets.length) return;

          dispatch(applyFontAction(updateWidgets));
        };

        const updateTemplateColors = () => {
          if (!colors?.length) return;

          const state = getState();
          const colorShuffle = new ColorShuffle(new Map([]));
          const colorShuffleCache = getColorCache(state.infograph.id);
          const { updatePageColors, updateWidgets } =
            colorShuffleCache || colorShuffle.shuffle(colors, state.infograph, undefined, undefined, seed);

          if (shouldCache) addColorCache({ updatePageColors, updateWidgets }, state.infograph.id);

          dispatch(applyColor({ updatePageColors, updateWidgets, colors }));
          dispatchGlobalApplyThemeEvent();
        };

        await updateTemplateLogo(selectAllPageBackgroundDataFromData(pageId, infographState), seed);
        updateTemplateFont();
        updateTemplateColors();

        loadFonts(findFontsInPage(getState().infograph, pageId), dispatch(setFontsLoaded(true))).finally(() => {
          dispatch(setFontsLoaded(true));
        });

        // setting the page at the end of the process fix images not loading in some cases
        dispatch(setActivePage({ pageId }));
      } catch (error) {
        console.error('Error loading brand template data', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData(customStore ?? store);
  }, [brandTemplateData, customStore, setHasError, setIsLoading, updateTemplateLogo]);

  return <>{children}</>;
};

type BrandTemplatePreviewContainerProps = {
  customStore?: any;
  targetHeight?: number;
};

export const BrandTemplatePreviewContainer = ({ customStore, targetHeight }: BrandTemplatePreviewContainerProps) => {
  const [scale, setScale] = useState(DEFAULT_PREVIEW_SCALE);
  const containerRef = useRef<HTMLDivElement>(null);
  const widthPx = useAppSelector(selectInfographWidthPx);
  const heightPx = useAppSelector(selectInfographHeightPx);
  const activePageId = useAppSelector(selectActivePage);
  const pageBackground = useAppSelector(selectPageBackgroundString(activePageId));
  const { isLoading, hasError, brandTemplateData } = useBrandTemplatePreview();

  const templateHeight = targetHeight || brandTemplateData?.targetHeight;

  useEffect(() => {
    if (!!templateHeight) {
      const targetWidth = templateHeight * MAX_THUMBNAIL_DIMENSIONS_RATIO;
      const widthRatio = targetWidth / widthPx;
      const heightRatio = templateHeight / heightPx;
      setScale(Math.max(widthRatio, heightRatio));
      return;
    }

    const containerWidth = containerRef.current?.clientWidth;
    const containerHeight = containerRef.current?.clientHeight;
    if (!containerWidth || !containerHeight || !widthPx || !heightPx) return;
    const widthRatio = containerWidth / widthPx;
    const heightRatio = containerHeight / heightPx;
    setScale(Math.min(widthRatio, heightRatio));
  }, [widthPx, heightPx, templateHeight]);

  return (
    <DesignPreviewLoader customStore={customStore ?? store}>
      <Center w='100%' minH={100} height='100%' ref={containerRef} position='relative'>
        {isLoading && (
          <AbsoluteCenter zIndex={1}>
            <Spinner speed='0.65s' size='lg' />
          </AbsoluteCenter>
        )}
        {activePageId && !isLoading && (
          <Page
            testId={activePageId}
            className='page-container'
            bg={pageBackground}
            width={widthPx}
            height={heightPx}
            zoom={scale}
            borderRadius='none'
            boxShadow='none'
            overflow='hidden'
          >
            <ReadOnlyStructureTreeListWidgetRenderer pageId={activePageId} />
          </Page>
        )}
        {/* TODO: edge case, fallback to thumbnail placeholder */}
        {hasError && !activePageId && 'Preview is not available'}
      </Center>
    </DesignPreviewLoader>
  );
};

const BrandTemplatePreview = (): ReactElement => {
  return (
    <Provider store={store}>
      <BrandTemplatePreviewProvider>
        <BrandTemplatePreviewContainer />
      </BrandTemplatePreviewProvider>
    </Provider>
  );
};

export default BrandTemplatePreview;
